import {useRef, useState} from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import {calendarTimeAllocationHeaderToolbar} from '../constants'
import {EventClickArg} from '@fullcalendar/core'
import Modal from 'components/ui/Modal'
import TextField from 'components/ui/TextField'
import Button from 'components/ui/Button'
import {formatUSDate} from 'utils/intlDate'
import InputAdornment from '@mui/material/InputAdornment'
import {
    useCreateTruckCapacityOverrideMutation,
    useGetLocationAvailabilityQuery,
    useUpdateTruckOverrideMutation
} from 'services/classes'
import {format} from 'date-fns'
import {enqueueSnackbar} from 'notistack' // Import toast notification

// Transform the payload into FullCalendar-compatible events
const transformPayloadToEvents = (payload: any) => {
    const events: any = [];

    if (!payload) {
        return []
    }

    payload.forEach((truck: any) => {
        const truckId = truck.truck_id;
        const truckName = truck.truck_name;

        Object.entries(truck.availability).forEach(([date, dayAvailability]: any) => {
            dayAvailability.forEach((availability: any) => {
                events.push({
                    title: `Truck ${truckName} / ${availability.truck_type} - ${availability.minutes_available} min`,
                    start: format(new Date(date), 'yyyy-MM-dd'), // Ensure the date format is correct
                    allDay: true,
                    backgroundColor: availability.overrided ? '#FF0000' : '#91D0FF', // Differentiate override days
                    borderColor: availability.overrided ? '#FF0000' : '#91D0FF',
                    textColor: availability.overrided ? '#000' : '#002133',
                    extendedProps: {
                        truckId,
                        truckName,
                        minutesAvailable: availability.minutes_available,
                        locationId: availability.location_id,
                        overrideExists: availability.overrided,
                        overrideId: availability.override_id,
                    },
                });
            });
        });
    });

    return events;
};

export default function LocationTimeAllocationCalendar({locationId, currentMonth, currentYear}: any) {
    const calendarRef2 = useRef<any>(null)
    const [open, setOpen] = useState(false)
    const [modalInfo, setModalInfo] = useState<any | null>(null)
    const [truck, setTruck] = useState<any>({})

    const [currentSelectedMonth, setCurrentSelectedMonth] = useState(currentMonth)
    const [currentSelectedYear, setCurrentSelectedYear] = useState(currentYear)

    const {data: truckPayload, refetch: refetchAvailability} =
        useGetLocationAvailabilityQuery({
            locationId: locationId,
            month: currentSelectedMonth,
            year: currentSelectedYear
        })
    const events = transformPayloadToEvents(truckPayload);


    // Mutation hooks
    const [createTruckCapacityOverride] = useCreateTruckCapacityOverrideMutation()
    const [updateTruckOverride] = useUpdateTruckOverrideMutation()

    const handleEventClick = async (clickInfo: EventClickArg) => {
        const trucksData: any = clickInfo.event.extendedProps || []
        const locationId: any = clickInfo.event.extendedProps.locationId

        if (trucksData.overrideExists) {
            setTruck({
                id: trucksData.truckId || '',
                title: trucksData.truckName || '',
                value: trucksData.minutesAvailable || trucksData.overrideMinutes,
                locationId: locationId,
                overrideExists: true,
                overrideId: trucksData.overrideId || null
            })
        } else {
            setTruck({
                id: trucksData.truckId || '',
                title: trucksData.truckName || '',
                value: trucksData.minutesAvailable
                    ? trucksData.minutesAvailable.toString()
                    : '',
                locationId: locationId,
                overrideExists: false
            })
        }

        setModalInfo({
            title: clickInfo.event.title || 'Truck',
            date: clickInfo.event.start || new Date()
        })

        setOpen(true)

    }

    const handleTruckValueChange = (newValue: string) => {
        setTruck((prevTruck: any) => ({
            ...prevTruck,
            value: newValue
        }))
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        const payload = {
            id: truck.overrideId ? truck.overrideId : undefined,
            class_id: truck.id,
            location_id: truck.locationId,
            override_minutes: parseInt(truck.value, 10),
            date_of_override: format(modalInfo?.date || new Date(), 'yyyy-MM-dd')
        }
        console.log({
            id: truck.overrideId ? truck.overrideId : undefined,
            class_id: truck.id,
            location_id: truck.locationId,
            override_minutes: parseInt(truck.value, 10),
            date_of_override: format(modalInfo?.date || new Date(), 'yyyy-MM-dd')
        })

        try {
            if (truck.overrideExists && truck.overrideId) {
                await updateTruckOverride({id: truck.overrideId, body: payload})
                enqueueSnackbar('Override updated successfully', {variant: 'success'})
            } else {
                await createTruckCapacityOverride({body: payload})
                enqueueSnackbar('Override created successfully', {variant: 'success'})
            }

            refetchAvailability()
            setOpen(false)
        } catch (error) {
            enqueueSnackbar('Failed to save override. Please try again.', {
                variant: 'error'
            })
        }
    }


    const handleDatesSet = (dateInfo: any) => {
        if (calendarRef2.current) {
            const calendarApi = calendarRef2.current.getApi()
            const currentDate = calendarApi.getDate()

            const currentMonth = currentDate.getMonth() + 1
            const currentYear = currentDate.getFullYear()
            setCurrentSelectedMonth(currentMonth);
            setCurrentSelectedYear(currentYear);

        }
    };
    return (
        <>
            <FullCalendar
                ref={calendarRef2}
                events={events}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={calendarTimeAllocationHeaderToolbar}
                eventClick={handleEventClick}
                eventBackgroundColor="#91D0FF"
                eventBorderColor="#91D0FF"
                eventTextColor="#002133"
                displayEventTime={false}
                eventDisplay="block"
                dayMaxEvents={4}
                datesSet={handleDatesSet}
            />
            <Modal
                title={modalInfo?.title || ''}
                size="xs"
                showHeader={false}
                body={
                    <div className="text-[#20232A] flex flex-col gap-8">
                        <div className="flex flex-col gap-2">
                            <h3 className="font-semibold text-2xl leading-4">
                                {modalInfo?.title}
                            </h3>
                            <p className="text-base leading-4">
                                {formatUSDate(modalInfo?.date || new Date())}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2" key={truck.id}>
                            <label className="font-semibold">
                                {truck.title} {truck.overrideExists ? '(Override)' : ''}
                            </label>
                            <TextField
                                cc-width="full"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">Minutes</InputAdornment>
                                    )
                                }}
                                id={`input-${truck.id}`}
                                value={truck.value}
                                onChange={(e) => handleTruckValueChange(e.target.value)}
                            />
                        </div>
                    </div>
                }
                actions={
                    <>
                        <Button
                            type="button"
                            cc-width="full"
                            onClick={() => setOpen(false)}
                        >
                            Discard
                        </Button>
                        <Button
                            type="button"
                            cc-width="full"
                            cc-color="purple"
                            onClick={handleSubmit}
                        >
                            {truck.overrideExists ? 'Update Override' : 'Create Override'}
                        </Button>
                    </>
                }
                open={open}
                onClose={() => setOpen(false)}
            />
        </>
    )
}
